import React, { useState } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { WindFarm } from '../types';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  h2 {
    font-size: 24px;
    font-weight: 700;
    color: #1a1f3a;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
  }
  
  svg {
    width: 24px;
    height: 24px;
    color: #6b7280;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #0099ff;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #00d4ff 0%, #0099ff 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 153, 255, 0.3);
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface WindFarmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (farm: Omit<WindFarm, 'id' | 'createdAt'>) => void;
    farm?: WindFarm;
}

const WindFarmModal: React.FC<WindFarmModalProps> = ({ isOpen, onClose, onSave, farm }) => {
    const [formData, setFormData] = useState({
        name: farm?.name || '',
        city: farm?.city || '',
        country: farm?.country || 'Turkey',
        lat: farm?.location.lat || 39.0,
        lng: farm?.location.lng || 35.0,
        turbineCount: farm?.turbineCount || 0,
        installedPower: farm?.installedPower || 0,
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        onSave({
            name: formData.name,
            city: formData.city,
            country: formData.country,
            location: { lat: formData.lat, lng: formData.lng },
            turbineCount: formData.turbineCount,
            installedPower: formData.installedPower,
        });

        onClose();
    };

    const handleChange = (field: string, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (!isOpen) return null;

    return (
        <Overlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <Header>
                    <h2>{farm ? 'Edit Wind Farm' : 'Add Wind Farm'}</h2>
                    <CloseButton onClick={onClose}>
                        <X />
                    </CloseButton>
                </Header>

                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label>Wind Farm Name *</Label>
                        <Input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="e.g. Demo RES"
                            required
                        />
                    </FormGroup>

                    <FormRow>
                        <FormGroup>
                            <Label>City *</Label>
                            <Input
                                type="text"
                                value={formData.city}
                                onChange={(e) => handleChange('city', e.target.value)}
                                placeholder="e.g. İzmir"
                                required
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Country *</Label>
                            <Input
                                type="text"
                                value={formData.country}
                                onChange={(e) => handleChange('country', e.target.value)}
                                placeholder="e.g. Turkey"
                                required
                            />
                        </FormGroup>
                    </FormRow>

                    <FormRow>
                        <FormGroup>
                            <Label>Latitude</Label>
                            <Input
                                type="number"
                                step="0.000001"
                                value={formData.lat}
                                onChange={(e) => handleChange('lat', parseFloat(e.target.value))}
                                placeholder="e.g. 39.0"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Longitude</Label>
                            <Input
                                type="number"
                                step="0.000001"
                                value={formData.lng}
                                onChange={(e) => handleChange('lng', parseFloat(e.target.value))}
                                placeholder="e.g. 35.0"
                            />
                        </FormGroup>
                    </FormRow>

                    <FormRow>
                        <FormGroup>
                            <Label>Turbine Count</Label>
                            <Input
                                type="number"
                                value={formData.turbineCount}
                                onChange={(e) => handleChange('turbineCount', parseInt(e.target.value))}
                                placeholder="e.g. 10"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Installed Power (MW)</Label>
                            <Input
                                type="number"
                                step="0.1"
                                value={formData.installedPower}
                                onChange={(e) => handleChange('installedPower', parseFloat(e.target.value))}
                                placeholder="e.g. 50"
                            />
                        </FormGroup>
                    </FormRow>

                    <ButtonGroup>
                        <Button type="button" $variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" $variant="primary">
                            {farm ? 'Save Changes' : 'Add Wind Farm'}
                        </Button>
                    </ButtonGroup>
                </Form>
            </ModalContainer>
        </Overlay>
    );
};

export default WindFarmModal;
